import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import * as api from "../services/api";
import { useAuth } from "./AuthContext";
import { PLATFORM_BRAND } from "../config/platformBrand";
import { applyBrandTheme, applyPlatformTheme } from "../utils/applyBrandTheme";

interface Branding {
  logoUrl?: string;
  homeTitle: string;
  homeSubtitle: string;
  primaryColor?: string;
  secondaryColor?: string;
}

interface BrandingContextType {
  branding: Branding;
  refreshBranding: () => void;
  isLoading: boolean;
  /** Aplica colores de un comercio sin recargar todo el branding */
  applyOrganizationTheme: (colors: {
    primaryColor?: string | null;
    secondaryColor?: string | null;
  }) => void;
}

const defaultBranding: Branding = {
  homeTitle: PLATFORM_BRAND.productName,
  homeSubtitle: PLATFORM_BRAND.tagline,
  primaryColor: PLATFORM_BRAND.primaryColor,
  secondaryColor: PLATFORM_BRAND.secondaryColor,
};

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export const BrandingProvider: React.FC<{
  children: React.ReactNode;
  artistId?: string;
}> = ({ children, artistId: propArtistId }) => {
  const { artist } = useAuth();
  const artistId = propArtistId || artist?.id;

  const [branding, setBranding] = useState<Branding>(defaultBranding);
  const [isLoading, setIsLoading] = useState(true);

  const applyOrganizationTheme = useCallback(
    (colors: { primaryColor?: string | null; secondaryColor?: string | null }): void => {
      applyBrandTheme({
        primaryColor: colors.primaryColor || PLATFORM_BRAND.primaryColor,
        secondaryColor: colors.secondaryColor || PLATFORM_BRAND.secondaryColor,
      });
    },
    [],
  );

  const fetchBranding = useCallback(async (): Promise<void> => {
    // Sin organización: identidad de la plataforma (no el primer registro de la BD)
    if (!artistId) {
      setBranding(defaultBranding);
      applyPlatformTheme();
      setIsLoading(false);
      return;
    }

    try {
      const data = await api.getBranding(artistId);
      if (data) {
        const logoUrl = api.getAssetUrl(data.logoUrl) || undefined;
        const primaryColor =
          data.primaryColor || PLATFORM_BRAND.primaryColor;
        const secondaryColor =
          data.secondaryColor || PLATFORM_BRAND.secondaryColor;

        setBranding({
          logoUrl,
          homeTitle: data.homeTitle || PLATFORM_BRAND.productName,
          homeSubtitle: data.homeSubtitle || PLATFORM_BRAND.tagline,
          primaryColor,
          secondaryColor,
        });

        applyBrandTheme({ primaryColor, secondaryColor });

        if (logoUrl) {
          try {
            const links = document.querySelectorAll("link[rel*='icon']");
            links.forEach((link) => {
              (link as HTMLLinkElement).href = logoUrl;
            });
            if (links.length === 0) {
              const link = document.createElement("link");
              link.rel = "icon";
              link.href = logoUrl;
              document.head.appendChild(link);
            }
          } catch (e) {
            console.warn("Favicon update failed", e);
          }
        }

        if (data.homeTitle) {
          document.title = data.homeTitle;
        }
      } else {
        setBranding(defaultBranding);
        applyPlatformTheme();
      }
    } catch (error) {
      console.error("Error fetching branding:", error);
      setBranding(defaultBranding);
      applyPlatformTheme();
    } finally {
      setIsLoading(false);
    }
  }, [artistId]);

  useEffect(() => {
    void fetchBranding();
  }, [fetchBranding]);

  return (
    <BrandingContext.Provider
      value={{
        branding,
        refreshBranding: fetchBranding,
        isLoading,
        applyOrganizationTheme,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = (): BrandingContextType => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return context;
};
